import { useRef, useCallback } from 'react';

export default function DriverSwipeControl({ label, disabled, updating, onComplete, text }) {
  const trackRef = useRef(null);
  const swipeRef = useRef(null);
  const progressRef = useRef(0);
  const draggingRef = useRef(false);
  const pointerIdRef = useRef(null);
  const metricsRef = useRef(null);

  const applyProgress = useCallback((progress) => {
    const clamped = Math.max(0, Math.min(1, progress));
    progressRef.current = clamped;
    const track = trackRef.current;
    const swipe = swipeRef.current;
    if (!track || !swipe) return;
    const width = metricsRef.current?.width || track.getBoundingClientRect().width;
    const travel = Math.max(0, width - 62);
    swipe.style.setProperty('--swipe-progress', String(clamped));
    swipe.style.setProperty('--swipe-thumb-x', `${Math.round(travel * clamped * 10) / 10}px`);
    swipe.style.setProperty('--swipe-fill-width', `${Math.round((62 + travel * clamped) * 10) / 10}px`);
  }, []);

  const calculateProgress = useCallback((clientX) => {
    const track = trackRef.current;
    if (!track) return 0;
    const rect = metricsRef.current || track.getBoundingClientRect();
    return Math.max(0, Math.min(1, (clientX - rect.left - 31) / Math.max(1, rect.width - 62)));
  }, []);

  const startDrag = (event) => {
    if (disabled || updating) return;
    const rect = event.currentTarget.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.left + 62) return;
    event.preventDefault();
    metricsRef.current = { left: rect.left, width: rect.width };
    pointerIdRef.current = event.pointerId;
    draggingRef.current = true;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    applyProgress(calculateProgress(event.clientX));
  };

  const moveDrag = (event) => {
    if (!draggingRef.current || event.pointerId !== pointerIdRef.current) return;
    event.preventDefault();
    applyProgress(calculateProgress(event.clientX));
  };

  const finishDrag = async (event) => {
    if (!draggingRef.current || event.pointerId !== pointerIdRef.current) return;
    const finalProgress = calculateProgress(event.clientX);
    draggingRef.current = false;
    pointerIdRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    if (finalProgress >= 0.95) {
      applyProgress(1);
      await onComplete();
    }
    applyProgress(0);
    metricsRef.current = null;
  };

  const cancelDrag = () => {
    draggingRef.current = false;
    pointerIdRef.current = null;
    metricsRef.current = null;
    applyProgress(0);
  };

  return (
    <div
      ref={swipeRef}
      className={`driver-status-swipe ${updating ? 'is-updating' : ''}`}
      style={{ '--swipe-progress': 0, '--swipe-thumb-x': '0px', '--swipe-fill-width': '62px' }}
    >
      <div
        ref={trackRef}
        className="driver-status-swipe-track"
        role="button"
        tabIndex={disabled || updating ? -1 : 0}
        aria-disabled={disabled || updating}
        aria-label={label}
        onKeyDown={event => { if (["Enter", " "].includes(event.key) && !disabled && !updating) { event.preventDefault(); onComplete(); } }}
        onPointerDown={startDrag}
        onPointerMove={moveDrag}
        onPointerUp={finishDrag}
        onPointerCancel={cancelDrag}
      >
        <div className="driver-status-swipe-fill" />
        <div className="driver-status-swipe-copy">
          <span>{updating ? text.applyingOrder : label}</span>
          <small>{text.swipeToConfirm}</small>
        </div>
        <div className="driver-status-swipe-thumb" aria-hidden="true">
          <span>›</span>
        </div>
      </div>
    </div>
  );
}
